import { useState } from "react";
import { Loader2, Mail } from "lucide-react";
import { z } from "zod";
import { supabase } from "@/integrations/supabase/client";
import { signInWithProvider } from "@/utils/nativeAuth";
import { PleasantLogo } from "@/components/PleasantLogo";
import { ConfirmEmailSent } from "@/components/ConfirmEmailSent";
import { toast } from "sonner";
import {
  isRepeatSignup,
  REPEAT_SIGNUP_DESCRIPTION,
  REPEAT_SIGNUP_TITLE,
} from "@/lib/repeatSignup";

const SKIP_KEY = "pleasant.skipSignIn.v1";
export const markSignInSkipped = () => localStorage.setItem(SKIP_KEY, "1");
export const wasSignInSkipped = () => localStorage.getItem(SKIP_KEY) === "1";

const emailSchema = z.string().trim().email("Enter a valid email").max(255);
const passwordSchema = z.string().min(8, "At least 8 characters").max(72);

interface Props {
  /** Called when the user successfully authenticates OR taps "Skip for now". */
  onDone: () => void;
}

/**
 * Lightweight sign-in prompt shown right after onboarding. Optional —
 * the user can skip and continue using the app anonymously. We persist
 * the skip so we don't keep nagging them.
 */
export const QuickSignIn = ({ onDone }: Props) => {
  const [mode, setMode] = useState<"signin" | "signup">("signup");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [busy, setBusy] = useState(false);
  const [showEmail, setShowEmail] = useState(false);
  /** Set once the confirmation email is on its way. The form must NOT re-arm
   *  after a successful sign-up: two real users double-tapped on launch
   *  morning and every re-tap silently retired the previous link (Aug 20,
   *  see ConfirmEmailSent.tsx for the full story). */
  const [confirmSent, setConfirmSent] = useState<{ email: string; redirect: string } | null>(null);

  const skip = () => {
    markSignInSkipped();
    onDone();
  };

  const handleOAuth = async (provider: "google" | "apple") => {
    if (busy) return;
    setBusy(true);
    try {
      const result = await signInWithProvider(provider);
      if (result.error) {
        toast.error(`Couldn't sign in with ${provider}`);
        setBusy(false);
        return;
      }
      // Web: window redirects. Native: in-app browser opens and the
      // pleasant://auth/callback deep link primes the session.
    } catch {
      toast.error("Sign-in failed");
      setBusy(false);
    }
  };

  /** Mail a reset link — same rules as Auth.tsx / QuickAuthDialog.tsx:
   *  HTTPS link (inbox-openable), identical toast whether or not the
   *  address has an account. */
  const handleForgotPassword = async () => {
    if (busy) return;
    try {
      const parsedEmail = emailSchema.parse(email);
      setBusy(true);
      const isNative = (window as Window & { Capacitor?: { isNativePlatform?: () => boolean } })
        .Capacitor?.isNativePlatform?.() ?? false;
      const redirect = isNative
        ? "https://pleasantroutes.com/reset"
        : `${window.location.origin}/reset`;
      const { error } = await supabase.auth.resetPasswordForEmail(parsedEmail, {
        redirectTo: redirect,
      });
      if (error) throw error;
      toast.success("Check your email", {
        description: "If that address has an account, a reset link is on its way.",
      });
    } catch (err: unknown) {
      const msg =
        err instanceof z.ZodError
          ? "Enter your email address first"
          : err instanceof Error
          ? err.message
          : "Something went wrong";
      toast.error(msg);
    } finally {
      setBusy(false);
    }
  };

  const handleEmail = async (e: React.FormEvent) => {
    e.preventDefault();
    if (busy) return;
    try {
      const parsedEmail = emailSchema.parse(email);
      const parsedPassword = passwordSchema.parse(password);
      setBusy(true);

      if (mode === "signup") {
        // emailRedirectTo MUST be HTTPS — see Auth.tsx / QuickAuthDialog.tsx
        // for the rationale. window.location.origin in the native shell
        // is `capacitor://localhost`, which iOS Mail won't open.
        const isNative = (window as Window & { Capacitor?: { isNativePlatform?: () => boolean } })
          .Capacitor?.isNativePlatform?.() ?? false;
        // /confirmed = dedicated "you're confirmed, open the app" landing.
        const emailRedirect = isNative
          ? "https://pleasantroutes.com/confirmed"
          : `${window.location.origin}/confirmed`;
        const { data, error } = await supabase.auth.signUp({
          email: parsedEmail,
          password: parsedPassword,
          options: { emailRedirectTo: emailRedirect },
        });
        if (error) throw error;
        // Existing confirmed account: Supabase sent nothing, so don't say
        // "check your inbox". Flip to sign-in with the email kept.
        if (isRepeatSignup(data.user)) {
          setMode("signin");
          toast(REPEAT_SIGNUP_TITLE, { description: REPEAT_SIGNUP_DESCRIPTION, duration: 8000 });
          return;
        }
        // Swap the form for the persistent "check your inbox" panel — a toast
        // alone vanished before slow inboxes delivered, and the re-armed form
        // invited the double-tap that killed the first link.
        setConfirmSent({ email: parsedEmail, redirect: emailRedirect });
      } else {
        const { error } = await supabase.auth.signInWithPassword({
          email: parsedEmail,
          password: parsedPassword,
        });
        if (error) throw error;
        toast.success("Welcome back");
        onDone();
      }
    } catch (err: unknown) {
      const msg =
        err instanceof z.ZodError
          ? err.errors[0]?.message ?? "Check your input"
          : err instanceof Error
          ? err.message
          : "Something went wrong";
      toast.error(msg);
    } finally {
      setBusy(false);
    }
  };

  return (
    <main className="min-h-screen bg-hero flex flex-col items-center justify-center px-6 py-10">
      <div className="w-full max-w-sm">
        <div className="flex flex-col items-center text-center mb-8">
          <PleasantLogo size="lg" withPin />
          <h1 className="heading text-3xl mt-4 mb-2">Save your trips</h1>
          <p className="text-sm text-muted-foreground leading-relaxed">
            Sign in to sync your places, preferences and pleasant finds across devices. Or skip — your choice.
          </p>
        </div>

        <div className="space-y-2">
          {/* Google + Apple buttons hidden — provider credentials can't be
           *  configured on the current Lovable-owned Supabase project.
           *  Email/password works without any console setup. */}
          {confirmSent ? (
            <ConfirmEmailSent
              email={confirmSent.email}
              emailRedirectTo={confirmSent.redirect}
              onChangeEmail={() => setConfirmSent(null)}
            />
          ) : !showEmail ? (
            <button
              type="button"
              onClick={() => setShowEmail(true)}
              className="w-full h-12 rounded-full bg-card border border-border flex items-center justify-center gap-2 text-sm font-medium hover:bg-muted/50 transition-smooth"
            >
              <Mail className="w-4 h-4" /> Continue with email
            </button>
          ) : (
            <form onSubmit={handleEmail} className="space-y-2 pt-1">
              <input
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="Email"
                autoComplete="email"
                required
                className="w-full h-11 px-4 rounded-full bg-card border border-border text-sm outline-none focus:ring-2 focus:ring-primary/40"
              />
              <input
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder="Password (8+ characters)"
                autoComplete={mode === "signup" ? "new-password" : "current-password"}
                required
                minLength={8}
                className="w-full h-11 px-4 rounded-full bg-card border border-border text-sm outline-none focus:ring-2 focus:ring-primary/40"
              />
              <button
                type="submit"
                disabled={busy}
                className="w-full h-11 rounded-full bg-pleasant text-primary-foreground text-sm font-semibold shadow-glow hover:brightness-110 transition-smooth disabled:opacity-60 flex items-center justify-center gap-2"
              >
                {busy && <Loader2 className="w-4 h-4 animate-spin" />}
                {mode === "signup" ? "Create account" : "Sign in"}
              </button>
              {mode === "signin" && (
                <p className="text-xs text-center pt-0.5">
                  <button
                    type="button"
                    onClick={handleForgotPassword}
                    disabled={busy}
                    className="text-muted-foreground hover:text-primary hover:underline"
                  >
                    Forgot your password?
                  </button>
                </p>
              )}
              <p className="text-xs text-center text-muted-foreground pt-1">
                {mode === "signup" ? "Already have an account?" : "New here?"}{" "}
                <button
                  type="button"
                  onClick={() => setMode(mode === "signup" ? "signin" : "signup")}
                  className="text-primary hover:underline font-medium"
                >
                  {mode === "signup" ? "Sign in" : "Create one"}
                </button>
              </p>
            </form>
          )}
        </div>

        <button
          type="button"
          onClick={skip}
          className="w-full mt-6 h-12 rounded-full border border-border bg-card/60 text-sm font-medium text-foreground hover:bg-muted/60 transition-smooth"
        >
          Skip for now →
        </button>
        <p className="text-[11px] text-center text-muted-foreground mt-2">
          You can always sign in later.
        </p>
      </div>
    </main>
  );
};

const GoogleIcon = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" aria-hidden>
    <path fill="#4285F4" d="M23 12.27c0-.79-.07-1.54-.2-2.27H12v4.51h6.16c-.27 1.4-1.07 2.59-2.28 3.39v2.82h3.69C21.7 18.71 23 15.77 23 12.27z"/>
    <path fill="#34A853" d="M12 23c3.08 0 5.66-1.02 7.55-2.78l-3.69-2.82c-1.02.69-2.33 1.1-3.86 1.1-2.97 0-5.49-2-6.39-4.7H1.83v2.95C3.71 20.45 7.56 23 12 23z"/>
    <path fill="#FBBC05" d="M5.61 13.8c-.23-.69-.36-1.43-.36-2.2s.13-1.51.36-2.2V6.45H1.83C1.05 7.97.6 9.69.6 11.5s.45 3.53 1.23 5.05l3.78-2.95z"/>
    <path fill="#EA4335" d="M12 5.5c1.68 0 3.18.58 4.36 1.71l3.27-3.27C17.65 2.04 15.07 1 12 1 7.56 1 3.71 3.55 1.83 7.45l3.78 2.95C6.51 7.5 9.03 5.5 12 5.5z"/>
  </svg>
);

const AppleIcon = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" aria-hidden fill="currentColor">
    <path d="M16.365 12.84c-.024-2.43 1.987-3.604 2.077-3.66-1.131-1.652-2.892-1.879-3.518-1.905-1.498-.151-2.927.882-3.69.882-.766 0-1.94-.86-3.193-.836-1.643.024-3.157.954-4.001 2.422-1.706 2.957-.436 7.337 1.227 9.74.81 1.171 1.776 2.487 3.045 2.44 1.222-.05 1.683-.79 3.16-.79 1.476 0 1.892.79 3.184.764 1.314-.024 2.146-1.193 2.95-2.37.93-1.36 1.314-2.677 1.337-2.745-.029-.013-2.567-.985-2.578-3.942zM13.79 5.49c.676-.82 1.131-1.96.99-3.106-.957.04-2.118.638-2.815 1.453-.625.72-1.173 1.872-1.025 2.99 1.07.083 2.165-.541 2.85-1.337z"/>
  </svg>
);
